import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Pencil, Plus, Printer as PrinterIcon, PlugZap, Trash2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { usePrinters, type PrinterItem, type PrinterList } from '@/components/printing';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Switch } from '@/components/ui/primitives';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

interface Form {
  id: string | null;
  name: string;
  uri: string;
  location: string;
  username: string;
  password: string;
  hasPassword: boolean;
  removePassword: boolean;
  media: string;
  copies: number;
  verifyTls: boolean;
  defaultFor: string[];
  active: boolean;
}
const empty: Form = { id: null, name: '', uri: 'ipp://', location: '', username: '', password: '', hasPassword: false, removePassword: false, media: '', copies: 1, verifyTls: true, defaultFor: [], active: true };

const MEDIA_SUGGESTIONS = [
  { value: '', label: 'Druckerstandard' },
  { value: 'iso_a4_210x297mm', label: 'A4' },
  { value: 'iso_a5_148x210mm', label: 'A5' },
  { value: 'om_62x29mm_62x29mm', label: 'Etikett 62 × 29 mm' },
  { value: 'om_62x100mm_62x100mm', label: 'Etikett 62 × 100 mm' },
];

const onError = (e: unknown) => toast.error(errorMessage(e));

function PrinterDialog({ form, onClose, documents }: { form: Form | null; onClose: () => void; documents: PrinterList['documents'] }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Form>(form ?? empty);
  const [last, setLast] = useState<Form | null>(null);
  if (form !== last) {
    setLast(form);
    if (form) setF(form);
  }
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        uri: f.uri.trim(),
        location: f.location || null,
        username: f.username || null,
        password: f.removePassword ? null : f.password || undefined,
        media: f.media || null,
        copies: f.copies,
        verifyTls: f.verifyTls,
        defaultFor: f.defaultFor,
        active: f.active,
      };
      return f.id ? api<PrinterList>(`/printers/${f.id}`, { method: 'PUT', body }) : api<PrinterList>('/printers', { method: 'POST', body });
    },
    onSuccess: (v) => {
      qc.setQueryData(['printers'], v);
      toast.success('Drucker gespeichert');
      onClose();
    },
    onError,
  });
  return (
    <Dialog open={form !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent wide>
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{f.id ? 'Drucker bearbeiten' : 'Drucker hinzufügen'}</DialogTitle>
            <DialogDescription>Netzwerkdrucker mit IPP (fast alle aktuellen Drucker) oder eine Warteschlange des Druckservers (CUPS).</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" htmlFor="p-name">
              <Input id="p-name" required value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="z. B. Etiketten Theke" />
            </Field>
            <Field label="Standort" htmlFor="p-loc">
              <Input id="p-loc" value={f.location} onChange={(e) => set('location', e.target.value)} placeholder="z. B. Annahme" />
            </Field>
            <Field
              label="Adresse"
              htmlFor="p-uri"
              className="sm:col-span-2"
              hint="Direkt: ipp://192.168.1.50/ipp/print · über den Druckserver: ipp://cups:631/printers/Etiketten · verschlüsselt: ipps://…"
            >
              <Input id="p-uri" required value={f.uri} onChange={(e) => set('uri', e.target.value)} className="font-mono" />
            </Field>
            <Field label="Papierformat" htmlFor="p-media" hint="IPP-Schlüsselwort; „Verbindung prüfen“ zeigt die vom Drucker unterstützten.">
              <Input id="p-media" list="p-media-list" value={f.media} onChange={(e) => set('media', e.target.value)} placeholder="Druckerstandard" className="font-mono" />
              <datalist id="p-media-list">
                {MEDIA_SUGGESTIONS.filter((m) => m.value).map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </datalist>
            </Field>
            <Field label="Exemplare (Standard)" htmlFor="p-copies">
              <Input id="p-copies" type="number" min={1} max={20} value={f.copies} onChange={(e) => set('copies', Math.min(20, Math.max(1, Number(e.target.value) || 1)))} className="w-24" />
            </Field>
            <Field label="Benutzername (optional)" htmlFor="p-user">
              <Input id="p-user" value={f.username} onChange={(e) => set('username', e.target.value)} autoComplete="off" />
            </Field>
            <Field label="Passwort (optional)" htmlFor="p-pass" hint={f.hasPassword ? 'Leer lassen = unverändert' : undefined}>
              <Input id="p-pass" type="password" value={f.password} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" disabled={f.removePassword} />
            </Field>
            {f.hasPassword && (
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <Checkbox checked={f.removePassword} onCheckedChange={(v) => set('removePassword', Boolean(v))} /> Gespeichertes Passwort entfernen
              </label>
            )}
            <fieldset className="grid gap-2 sm:col-span-2">
              <legend className="mb-1 text-sm font-medium">Standarddrucker für</legend>
              <div className="flex flex-wrap gap-3">
                {documents.map((d) => (
                  <label key={d.key} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={f.defaultFor.includes(d.key)}
                      onCheckedChange={(v) => set('defaultFor', v ? [...f.defaultFor, d.key] : f.defaultFor.filter((x) => x !== d.key))}
                    />
                    {d.label}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.verifyTls} onCheckedChange={(v) => set('verifyTls', v)} /> TLS-Zertifikat prüfen (ipps://)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.active} onCheckedChange={(v) => set('active', v)} /> Aktiv
            </label>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!f.name.trim() || f.uri.trim().length < 8}>
              Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PrintersPage() {
  const qc = useQueryClient();
  const { data } = usePrinters();
  const { data: jobs } = useQuery({
    queryKey: ['printers', 'jobs'],
    queryFn: () => api<{ items: { id: string; title: string; printer: string | null; status: 'SENT' | 'FAILED'; error: string | null; actorName: string | null; copies: number; createdAt: string }[] }>('/printers/jobs'),
  });
  const [edit, setEdit] = useState<Form | null>(null);
  const [confirmDialog, confirm] = useConfirm();
  const refresh = () => qc.invalidateQueries({ queryKey: ['printers'] });
  const check = useMutation({
    mutationFn: (id: string) => api<{ ok: boolean; status: string; model: string | null; pdf: boolean; mode: string; media: string[]; warning: string | null }>(`/printers/${id}/check`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.ok && !r.warning) toast.success(`Erreichbar: ${r.model ?? 'Drucker'} – ${r.status}`, { description: `Druck über: ${r.mode}`, duration: 10_000 });
      else if (r.ok) toast.warning(`Erreichbar: ${r.model ?? 'Drucker'} – ${r.status}`, { description: r.warning ?? undefined, duration: 15_000 });
      else toast.error(r.warning ?? 'Drucker nicht erreichbar', { duration: 10_000 });
      refresh();
    },
    onError,
  });
  const testPage = useMutation({
    mutationFn: (id: string) => api<{ ok: boolean; error: string | null }>(`/printers/${id}/test-page`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.ok) toast.success('Testseite gesendet');
      else toast.error(r.error ?? 'Druck fehlgeschlagen');
      refresh();
    },
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api<PrinterList>(`/printers/${id}`, { method: 'DELETE' }), onSuccess: (v) => qc.setQueryData(['printers'], v), onError });
  const docLabel = (k: string) => data?.documents.find((d) => d.key === k)?.label ?? k;
  const toForm = (p: PrinterItem): Form => ({ ...p, location: p.location ?? '', username: p.username ?? '', password: '', removePassword: false, media: p.media ?? '' });

  return (
    <div className="mx-auto grid max-w-6xl gap-4">
      {confirmDialog}
      <PageHeader
        title="Drucker"
        description="Belege direkt aus dem Portal drucken – manuell oder per Automatisierung (z. B. Etikett und Annahmeschein bei der Geräteannahme)."
        actions={
          <Button onClick={() => setEdit(empty)}>
            <Plus /> Drucker
          </Button>
        }
      />
      <Card>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Drucker</TH>
              <TH className="hidden md:table-cell">Standard für</TH>
              <TH>Status</TH>
              <TH className="w-48" />
            </TR>
          </THead>
          <TBody>
            {data?.items.length === 0 && (
              <EmptyRow colSpan={4}>
                <span className="inline-flex items-center gap-2">
                  <PrinterIcon className="size-4" /> Noch kein Drucker eingerichtet.
                </span>
              </EmptyRow>
            )}
            {data?.items.map((p) => (
              <TR key={p.id}>
                <TD>
                  <p className="font-medium">
                    {p.name} {!p.active && <Badge variant="outline">inaktiv</Badge>}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground">{p.uri}</p>
                  {p.location && <p className="text-xs text-muted-foreground">{p.location}</p>}
                </TD>
                <TD className="hidden md:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {p.defaultFor.map((d) => (
                      <Badge key={d} variant="secondary">
                        {docLabel(d)}
                      </Badge>
                    ))}
                  </div>
                </TD>
                <TD className="text-sm">
                  {p.status ?? <span className="text-muted-foreground">nicht geprüft</span>}
                  {p.lastError && <p className="max-w-72 text-xs text-destructive">{p.lastError}</p>}
                  {p.lastCheckedAt && <p className="text-xs text-muted-foreground">{formatDateTime(p.lastCheckedAt)}</p>}
                </TD>
                <TD>
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="size-8" title="Verbindung prüfen" aria-label={`${p.name}: Verbindung prüfen`} onClick={() => check.mutate(p.id)} disabled={check.isPending}>
                      <PlugZap className="size-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="size-8" title="Testseite drucken" aria-label={`${p.name}: Testseite drucken`} onClick={() => testPage.mutate(p.id)} disabled={testPage.isPending}>
                      <FileText className="size-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="size-8" aria-label={`${p.name} bearbeiten`} onClick={() => setEdit(toForm(p))}>
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive hover:text-destructive"
                      aria-label={`${p.name} löschen`}
                      onClick={async () => (await confirm({ title: `Drucker „${p.name}“ löschen?`, confirmLabel: 'Löschen', destructive: true })) && del.mutate(p.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Einrichtung</CardTitle>
          <CardDescription>Der Server muss den Drucker im Netzwerk erreichen können.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 text-sm">
          <p>
            <strong>Netzwerkdrucker mit IPP Everywhere / AirPrint</strong> (die meisten aktuellen Büro- und viele Etikettendrucker): Adresse <code className="font-mono">ipp://&lt;IP-Adresse&gt;/ipp/print</code>. Mit „Verbindung prüfen“ sehen Sie, ob der Drucker PDF annimmt.
          </p>
          <p>
            <strong>Drucker ohne PDF-Unterstützung oder per USB</strong>: den Druckserver starten (<code className="font-mono">docker compose --profile printing up -d</code>), den Drucker in dessen Weboberfläche (Port 631) mit Treiber anlegen und hier als <code className="font-mono">ipp://cups:631/printers/&lt;Name&gt;</code> eintragen.
          </p>
          <Alert>Aus Sicherheitsgründen sind nur Adressen in lokalen Netzen erlaubt (Einstellung <code className="font-mono">PRINT_ALLOWED_NETWORKS</code>).</Alert>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Druckprotokoll</CardTitle>
          <CardDescription>Die letzten 50 Aufträge</CardDescription>
        </CardHeader>
        <Table>
          <TBody>
            {jobs?.items.length === 0 && <EmptyRow colSpan={4}>Noch keine Druckaufträge.</EmptyRow>}
            {jobs?.items.map((j) => (
              <TR key={j.id}>
                <TD className="w-8">{j.status === 'SENT' ? <CheckCircle2 className="size-4 text-success" aria-label="gesendet" /> : <XCircle className="size-4 text-destructive" aria-label="fehlgeschlagen" />}</TD>
                <TD>
                  <p className="text-sm font-medium">
                    {j.title}
                    {j.copies > 1 ? ` (${j.copies}×)` : ''}
                  </p>
                  {j.error && <p className="text-xs text-destructive">{j.error}</p>}
                </TD>
                <TD className="text-sm">{j.printer ?? '–'}</TD>
                <TD className="whitespace-nowrap text-right text-xs text-muted-foreground">
                  {formatDateTime(j.createdAt)}
                  <br />
                  {j.actorName}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>
      <PrinterDialog form={edit} onClose={() => setEdit(null)} documents={data?.documents ?? []} />
    </div>
  );
}
